import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, ArrowLeft } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import type { Project, GridColumn } from '../types';
import {
  FormState, EMPTY_FORM, VALUE_ADDED_OPTIONS, EFFORT_OPTIONS, TIMELINE_OPTIONS,
  formToPayload, projectToForm, countFilled,
  BareTextarea, AutosuggestInput, TagInput,
  CollaboratorsSection,
} from '../components/ProjectForm';
import { DeliverablesEditor } from '../components/DeliverablesEditor';

const DRAFT_KEY = 'pmo-submit-draft';

type SubmitTab = 'new' | 'submissions';

// Reads the active tab from the hash (#submit?tab=submissions) so a shared
// link can point straight at either tab, mirroring GerancePage's
// parseGeranceHash.
function parseSubmitHash(): SubmitTab {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return params.get('tab') === 'submissions' ? 'submissions' : 'new';
}

export function SubmitProjectPage() {
  const { user } = useAuth();
  const { t } = useT();

  const [form, setForm] = useState<FormState>(() => {
    try {
      const d = localStorage.getItem(DRAFT_KEY);
      return d ? JSON.parse(d) : { ...EMPTY_FORM };
    } catch {
      return { ...EMPTY_FORM };
    }
  });

  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [draftRestored, setDraftRestored] = useState(() => {
    try { return !!localStorage.getItem(DRAFT_KEY); } catch { return false; }
  });
  const [draftSaved, setDraftSaved] = useState(false);

  const [myInboxProjects, setMyInboxProjects] = useState<Project[]>([]);
  const [pipelineColumns, setPipelineColumns] = useState<GridColumn[]>([]);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState<SubmitTab>(() => parseSubmitHash());

  // ── Debounced draft auto-save ──
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const draftSavedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (editingProject) return;
    if (activeTab !== 'new') return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (form.name.trim()) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        setDraftSaved(true);
        clearTimeout(draftSavedTimerRef.current);
        draftSavedTimerRef.current = setTimeout(() => setDraftSaved(false), 2000);
      }
    }, 500);
    return () => clearTimeout(saveTimerRef.current);
  }, [form, editingProject, activeTab]);

  // ── Load projects & pipeline columns ──
  const loadProjects = useCallback(async () => {
    const [p, pipeCols] = await Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
    ]);
    setAllProjects(p);
    setPipelineColumns(pipeCols);
    const inboxCol = pipeCols[0];
    if (inboxCol && user) {
      setMyInboxProjects(p.filter(proj => proj.created_by === user.id && proj.status_id === inboxCol.id));
    }
  }, [user]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // ── Deep-link: #submit?edit=ID — the hash then stays a durable, shareable
  // address (kept in sync by the effect below) instead of collapsing back to
  // a bare #submit once the edit view opens.
  useEffect(() => {
    if (allProjects.length === 0 || !user) return;
    const hash = window.location.hash;
    const match = hash.match(/[?&]edit=(\d+)/);
    if (match) {
      const id = parseInt(match[1], 10);
      const project = allProjects.find(p => p.id === id);
      if (project && project.created_by === user.id) {
        setEditingProject(project);
        setForm(projectToForm(project));
        setActiveTab('submissions');
      }
    }
  }, [allProjects, user]);

  // Keep the hash in sync with the page's current view — the edit view's
  // project, or the New/My Submissions tab — via replaceState (not
  // pushState, so it doesn't spam the back-stack). The address bar is then
  // always a direct, shareable link back to the state in view.
  useEffect(() => {
    const newHash = editingProject
      ? `#submit?edit=${editingProject.id}`
      : (activeTab === 'submissions' ? '#submit?tab=submissions' : '#submit');
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [editingProject, activeTab]);

  // ── Form handlers ──
  const toggleValueAdded = (val: string) => {
    setForm({
      ...form,
      valueAdded: form.valueAdded.includes(val)
        ? form.valueAdded.filter(v => v !== val)
        : [...form.valueAdded, val],
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || submitting) return;
    setSubmitting(true);
    try {
      const payload = formToPayload(form);
      await api.createProject(payload);
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
      loadProjects();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    setForm({ ...EMPTY_FORM });
    localStorage.removeItem(DRAFT_KEY);
    setDraftRestored(false);
  };

  // ── Edit mode handlers ──
  const openEdit = (p: Project) => {
    setEditingProject(p);
    setForm(projectToForm(p));
  };

  const cancelEdit = () => {
    setEditingProject(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleSaveEdit = async () => {
    if (!editingProject || !form.name.trim()) return;
    setSubmitting(true);
    try {
      const payload = formToPayload(form);
      await api.updateProject(editingProject.id, payload);
      if (form.sponsor.trim()) api.createPerson(form.sponsor.trim()).catch(() => {});
      for (const ug of form.userGroups) api.createUserGroup(ug).catch(() => {});
      for (const c of form.collaborators) api.createPerson(c.name).catch(() => {});
      for (const infra of form.infrastructure) api.createInfrastructure(infra).catch(() => {});
      cancelEdit();
      localStorage.removeItem(DRAFT_KEY);
      loadProjects();
    } finally {
      setSubmitting(false);
    }
  };

  // ── Shared form fields ──
  const renderFormFields = () => (
    <>
      {/* ── PROBLEM ── */}
      <div className="form-section">
        <div className="section-label mb-12">{t('submitProject.sectionProblem')}</div>
        <div className="form-group">
          <label className="form-label">{t('submitProject.problemLabel')}</label>
          <BareTextarea value={form.problem} onChange={(v) => setForm({ ...form, problem: v })} placeholder={t('submitProject.problemPlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('submitProject.goalLabel')}</label>
          <BareTextarea value={form.goal} onChange={(v) => setForm({ ...form, goal: v })} placeholder={t('submitProject.goalPlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('submitProject.valueAddedLabel')}</label>
          <div className="selectable-group">
            {VALUE_ADDED_OPTIONS.map((val) => (
              <button key={val} className={`selectable${form.valueAdded.includes(val) ? ' active' : ''}`} onClick={() => toggleValueAdded(val)} type="button">{val}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── DELIVERABLE ── */}
      <div className="form-section">
        <div className="section-label mb-12">{t('submitProject.sectionDeliverable')}</div>
        <div className="form-group">
          <label className="form-label">{t('submitProject.deliverableLabel')}</label>
          <DeliverablesEditor deliverables={form.deliverables} onChange={(d) => setForm({ ...form, deliverables: d })} canEdit />
        </div>
      </div>

      {/* ── RESOURCES & EFFORT ── */}
      <div className="form-section">
        <div className="section-label mb-12">{t('submitProject.sectionResources')}</div>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">{t('submitProject.effortLabel')}</label>
            <div className="selectable-group">
              {EFFORT_OPTIONS.map((opt) => (
                <button key={opt} className={`selectable${form.effortSize === opt ? ' active' : ''}`} onClick={() => setForm({ ...form, effortSize: form.effortSize === opt ? '' : opt })} type="button">{opt}</button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('submitProject.timelineLabel')}</label>
            <div className="selectable-group">
              {TIMELINE_OPTIONS.map((opt) => (
                <button key={opt} className={`selectable${form.timeline === opt ? ' active' : ''}`} onClick={() => setForm({ ...form, timeline: form.timeline === opt ? '' : opt })} type="button">{opt}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">{t('submitProject.cashLabel')}</label>
          <input className="form-input--bare" value={form.cash} onChange={(e) => setForm({ ...form, cash: e.target.value })} placeholder={t('submitProject.cashPlaceholder')} />
        </div>
        <CollaboratorsSection form={form} setForm={setForm} canCreate={false} api={api} />
      </div>

      {/* ── OWNERSHIP ── */}
      <div className="form-section">
        <div className="section-label mb-12">{t('submitProject.sectionOwnership')}</div>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">{t('submitProject.sponsorLabel')}</label>
            <AutosuggestInput value={form.sponsor} onChange={(v) => setForm({ ...form, sponsor: v })} placeholder={t('submitProject.sponsorPlaceholder')} fetchSuggestions={async (q) => await api.getPeople(q) as any[]} bare />
          </div>
          <div className="form-group">
            <label className="form-label">{t('submitProject.userGroupsLabel')}</label>
            <TagInput tags={form.userGroups} onChange={(t2) => setForm({ ...form, userGroups: t2 })} placeholder={t('submitProject.tagInputPlaceholder')} fetchSuggestions={async (q) => await api.getUserGroups(q) as any[]} />
          </div>
        </div>
      </div>

    </>
  );

  // ── Success screen ──
  if (submitted) {
    return (
      <div className="page">
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-title">{t('submitProject.submittedTitle')}</div>
            <div className="empty-state-desc">
              {t('submitProject.submittedDesc')}
            </div>
            <button className="btn btn-accent mt-16" onClick={() => {
              setForm({ ...EMPTY_FORM });
              setSubmitted(false);
            }}>
              <Plus className="icon-sm" /> {t('submitProject.submitAnother')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Edit view (within submissions tab) ──
  if (editingProject) {
    return (
      <div className="page">
        <div className="initiative-form">
          <button className="form-back" onClick={cancelEdit}>
            <ArrowLeft className="icon-sm" /> {t('submitProject.backToSubmissions')}
          </button>

          <div className="form-section">
            <div className="section-label mb-8">{t('submitProject.editTitle')}</div>
            <input
              className="form-input--bare bare-title"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('submitProject.namePlaceholder')}
              autoFocus
            />
          </div>

          {renderFormFields()}

          <div className="form-footer">
            <button className="btn btn-ghost" onClick={cancelEdit}>{t('common.cancel')}</button>
            <button className="btn btn-accent" onClick={handleSaveEdit} disabled={!form.name.trim() || submitting}>
              {submitting ? t('submitProject.saving') : t('submitProject.saveChanges')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* ── Tabs ── */}
      {myInboxProjects.length > 0 && (
        <div className="inbox-tabs">
          <button
            className={`inbox-tab ${activeTab === 'new' ? 'inbox-tab--active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            {t('submitProject.tabNew')}
          </button>
          <button
            className={`inbox-tab ${activeTab === 'submissions' ? 'inbox-tab--active' : ''}`}
            onClick={() => setActiveTab('submissions')}
          >
            {t('submitProject.tabMySubmissions')}
            <span className="tab-badge">{myInboxProjects.length}</span>
          </button>
        </div>
      )}

      {/* ── New Submission tab ── */}
      {activeTab === 'new' && (
        <div className="initiative-form">
          {draftRestored && (
            <div className="draft-banner">
              <span>{t('submitProject.draftRestored')}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => {
                localStorage.removeItem(DRAFT_KEY);
                setForm({ ...EMPTY_FORM });
                setDraftRestored(false);
              }}>{t('submitProject.discard')}</button>
            </div>
          )}

          <div className="form-section">
            <div className="section-label mb-8">{t('submitProject.submitTitle')}</div>
            <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s16)' }}>
              {t('submitProject.submitIntro')}
            </p>
            <input
              className="form-input--bare bare-title"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('submitProject.namePlaceholder')}
              autoFocus
            />
          </div>

          {renderFormFields()}

          <div className="form-footer">
            {draftSaved && <span className="draft-indicator">{t('submitProject.draftSaved')}</span>}
            <button className="btn btn-ghost" onClick={handleClear}>{t('common.clear')}</button>
            <button className="btn btn-accent" onClick={handleSubmit} disabled={!form.name.trim() || submitting}>
              {submitting ? t('submitProject.submitting') : t('submitProject.submitForReview')}
            </button>
          </div>
        </div>
      )}

      {/* ── My Submissions tab ── */}
      {activeTab === 'submissions' && (
        <div className="flex flex-col gap-8">
          {myInboxProjects.map((project) => {
            const filled = countFilled(projectToForm(project));
            return (
              <div key={project.id} className="inbox-card" onClick={() => openEdit(project)}>
                <div className="inbox-card-header">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="inbox-card-name">{project.name}</div>
                    {project.goal && <div className="inbox-card-goal">{project.goal}</div>}
                  </div>
                </div>
                <div className="inbox-card-meta">
                  {project.project_code && <span className="badge">{project.project_code}</span>}
                  <span className="badge badge-accent">{t('submitProject.inInbox')}</span>
                  <div className="completion-bar ml-auto" title={t('submitProject.sectionsFilled', { filled })}>
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className={`completion-seg${i < filled ? ' filled' : ''}`} />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          {myInboxProjects.length === 0 && (
            <div className="empty-state-card">
              <div className="empty-state">
                <div className="empty-state-title">{t('submitProject.noSubmissionsTitle')}</div>
                <div className="empty-state-desc">
                  {t('submitProject.noSubmissionsDesc')}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
